import { AmountInfoBoxLocalized, Translate } from '@components'

import { AmountInfoBoxesGroup, AmountInfoBoxProps, Space, Text } from '@billy/design-system'
import { ReactElement, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { invoiceStatusToBadgeColor } from '@features/invoicesLegacy/constants/invoiceStatusToBadgeVariant'
import { InvoiceStatus } from '@features/invoicesLegacy/enums/invoiceStatus'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { useInvoicesListFilters } from '../../hooks/useInvoicesListFilters'
import { useInvoiceSummaryFiltered } from '../../hooks/useInvoicesSummaryFiltered'
import { getAmountForStatusAll } from '../../utils/getAmountForStatusAll'
import { getBadgeAmountForStatusAll } from '../../utils/getBadgeAmountForStatus'
import * as Styled from './styles'

const SUMMARY_BOXES_STATUSES: InvoiceStatus[] = [
  InvoiceStatus.All,
  InvoiceStatus.Draft,
  InvoiceStatus.Overdue,
  InvoiceStatus.Unpaid,
  InvoiceStatus.Paid,
]

type SummaryInfoBox = Pick<
  AmountInfoBoxProps<InvoiceStatus>,
  'active' | 'amount' | 'badgeAmount' | 'badgeLabel' | 'badgeColor' | 'id'
>

export const InvoicesSummary = (): ReactElement => {
  const { t } = useTranslation()
  const { organization } = useUserOrganization()

  const { data, isLoading } = useInvoiceSummaryFiltered()
  const [{ status: currentStatus }, setFilters] = useInvoicesListFilters()

  const handleClick = useCallback(
    (id: InvoiceStatus) => {
      setFilters({ page: 1, status: id })
    },
    [setFilters],
  )

  const infoBoxes: SummaryInfoBox[] = useMemo(
    () =>
      SUMMARY_BOXES_STATUSES.map((status) => {
        const { amount: dataAmount, count: dataCount } = data?.[status] || {}
        const badgeAmount = status === InvoiceStatus.All && data ? getBadgeAmountForStatusAll(data) : dataCount
        const amount = status === InvoiceStatus.All && data ? getAmountForStatusAll(data) : dataAmount

        return {
          active: status === currentStatus,
          amount,
          badgeAmount,
          badgeLabel: t(`invoices.category.${status}`, { count: badgeAmount }),
          badgeColor: invoiceStatusToBadgeColor[status],
          id: status,
        }
      }),
    [currentStatus, data, t],
  )

  return (
    <Styled.InvoicesSummaryWrapper>
      <Text variant="h4">
        <Translate value="bills.summary.title" />
      </Text>
      <Space size="s16" />
      <AmountInfoBoxesGroup itemsInRow={infoBoxes.length} loading={isLoading}>
        {infoBoxes.map(({ active, amount, badgeAmount, badgeLabel, badgeColor, id }) => (
          <AmountInfoBoxLocalized
            active={active}
            amount={amount}
            badgeAmount={badgeAmount}
            badgeLabel={badgeLabel}
            badgeColor={badgeColor}
            clickable
            currencyId={organization?.baseCurrencyId}
            id={id}
            key={id}
            onClick={handleClick}
          />
        ))}
      </AmountInfoBoxesGroup>
    </Styled.InvoicesSummaryWrapper>
  )
}
